import { saveCard } from '@api/card-api';
import { getUser } from '@api/user-api';
import { useMutation } from '@apollo/client/react';
import type { AnonymousUser, Card, CardImage, User } from '@blinq-types';
import { EditBlinqCardDocument } from '@graphql/mutations/cards/edit-card.graphql';
import { useFeatureFlag } from '@modules/feature-flag';

type EditCardInput =
  | { card: Card; userId: string; user?: never }
  | { card: Card; userId?: never; user: AnonymousUser | User };

function toCardImageInput(cardImage?: CardImage | null) {
  if (!cardImage) {
    return undefined;
  }

  return {
    edited_img_url: cardImage?.editedImageUrl,
    original_img_url: cardImage?.originalImageUrl,
    editor_metadata: {
      background_color: cardImage?.editorMetadata?.backgroundColor,
      created_at: cardImage?.editorMetadata?.createdAt,
      editor_version: cardImage?.editorMetadata?.editorVersion,
      updated_at: cardImage?.editorMetadata?.updatedAt,
    },
  };
}

export function useEditBlinqCard() {
  const [mutation, { loading: loadingMutation }] = useMutation(EditBlinqCardDocument);
  const [useGraphQl, { loading: loadingUseGraphQl }] = useFeatureFlag('schrodinger_edit_card', 'off');

  // Due to be deprecated. Uses Firestore.
  const oldEditBlinqCard = async (input: EditCardInput) => {
    const card = input.card;
    const user = input.user || (await getUser(input.userId));

    if (!user) {
      throw new Error('Failed to edit Blinq card. User not found.');
    }

    await saveCard(card, user);
  };

  // New implementation. Uses GraphQL.
  const newEditBlinqCard = async (input: EditCardInput) => {
    const card = input.card;
    const userId = (input.userId || input.user?.userId) as string; // Safe to cast.

    await mutation({
      variables: {
        input: {
          user_id: userId,
          card_id: card.cardId,
          layout_images_locked: card.layoutImagesLocked,
          color: card.color,
          headline: card.headline,
          all_field_types_locked: card.allFieldTypesLocked,
          urls: card.urls,
          maiden_name: card.maidenName,
          preferred_name: card.preferredName,
          first_name: card.firstName,
          pronouns: card.pronouns,
          phone_numbers: card.phoneNumbers,
          last_name: card.lastName,
          accreditations: card.accreditations,
          profile_picture_url: card.profilePictureImg?.editedImageUrl,
          blinq_message_enabled: card.blinqMessageEnabled,
          emails: card.emails,
          all_fields_locked: card.allFieldTypesLocked,
          color_locked: card.colorLocked,
          where_we_met_enabled: card.whereWeMetEnabled,
          field_order: card.fieldOrder,
          qr_code_locked: card.qrCodeLocked,
          layout: card.layout ? `Layout_${card.layout}` : undefined,
          company_logo_img: toCardImageInput(card.companyLogoImg),
          profile_picture_img: toCardImageInput(card.profilePictureImg),
          cover_photo_img: toCardImageInput(card.coverPhotoImg),
          job_title: card.jobTitle,
          honorific_prefix: card.honorificPrefix,
          card_title: card.cardTitle,
          middle_name: card.middleName,
          company_logo_url: card.companyLogoImg?.editedImageUrl,
          referral_link_enabled: card.referralLinkEnabled,
          creation_timestamp_ms: card.creationTimestampMs,
          updated_timestamp_ms: card.updatedTimestampMs,
          department: card.department,
          honorific_suffix: card.honorificSuffix,
          org_name: card.orgName,
          addresses: card.addresses,
          show_image_in_qr_code: card.logoIsInQr,
        },
      },
    });
  };

  return {
    editBlinqCard: useGraphQl === 'on' ? newEditBlinqCard : oldEditBlinqCard,
    loading: loadingMutation || loadingUseGraphQl,
  };
}
